"use client"
import React, { useEffect, useMemo, useState } from "react";
import { useConfig } from "@/lib/providers/ConfigProvider";
import AmenityViewList from "@/components/categories/AmenityViewList";
import useUtils from "@/components/shared/useUtils";
import PhotoswipeGallery from "@/components/shared/PhotoswipeGallery";
import CrmPauEnums from "@/components/crm/pau/CrmPauEnums";
import "@/components/crm/pau/re/CrmPauREListingView.css";
import { formatExpectedCompletion } from "@/components/crm/pau/re/reListingAvailability";

const CrmPauREListingView = ({ reListing }) => {
  const { apiUrl } = useConfig();

  const utils = useUtils();
  const listingTitle = useMemo(() => buildListingTitle(reListing), [reListing]);
  const addressLabel = useMemo(
    () =>
      [
        reListing?.addressSnapshot?.city,
        reListing?.addressSnapshot?.region,
        reListing?.addressSnapshot?.postcode
      ]
        .filter(Boolean)
        .join(", "),
    [reListing]
  );

  const saleOrLease =
    reListing?.saleOrLease ?? reListing?.unitSnapshot?.saleOrLease;
  const monthlyRent =
    saleOrLease === "LEASE"
      ? reListing?.monthlyRent ?? reListing?.unitSnapshot?.monthlyRent
      : null;
  const rentPerSqFtYear =
    saleOrLease === "LEASE"
      ? reListing?.rentPerSqFtYear ?? reListing?.unitSnapshot?.rentPerSqFtYear
      : null;
  const salePrice =
    saleOrLease === "SALE"
      ? reListing?.salePrice ?? reListing?.unitSnapshot?.salePrice
      : null;
  const underDevelopment = saleOrLease === "UNDER_DEVELOPMENT";
  const expectedCompletion = underDevelopment
    ? formatExpectedCompletion(reListing?.expectedCompletion)
    : null;
  const lotSize = reListing?.addressSnapshot?.lotSqFt;
  const status = formatEnumValue(reListing?.status);
  const headline = reListing?.heading?.trim() ?? "Description";
  const description = reListing?.description?.trim();
  const descriptionParagraphs = useMemo(() => {
    if (!description) {
      return [];
    }
    return description
      .split(/\n+/)
      .map(paragraph => paragraph.trim())
      .filter(Boolean);
  }, [description]);

  const statCards = useMemo(() => {
    if (!reListing) return [];

    const unit = reListing.unitSnapshot ?? {};
    const address = reListing.addressSnapshot ?? {};

    const entries = [];

    if (unit.type) {
      entries.push({
        label: "Use Type",
        value: formatEnumValue(unit.type)
      });
    }

    if (unit.areaSqFt) {
      entries.push({
        label: "Square Footage",
        value: `${Number(unit.areaSqFt).toLocaleString()} sq ft`
      });
    }

    if (address.yearBuilt) {
      entries.push({
        label: "Year Built",
        value: address.yearBuilt
      });
    }

    if (unit.floor) {
      entries.push({
        label: "Floor",
        value: formatEnumValue(unit.floor)
      });
    }

    if (reListing?.addressSnapshot?.stories) {
      entries.push({
        label: "Stories",
        value: reListing.addressSnapshot.stories
      });
    }

    if (unit.entranceFrom) {
      entries.push({
        label: "Entrance",
        value: formatEnumValue(unit.entranceFrom)
      });
    }

    if (unit.adaAccessible != null) {
      entries.push({
        label: "ADA Accessible",
        value: unit.adaAccessible ? "Yes" : "No"
      });
    }

    if (lotSize) {
      entries.push({
        label: "Lot Size",
        value: `${Number(lotSize).toLocaleString()} sq ft`
      });
    }

    return entries;
  }, [reListing, lotSize]);

  const brokerDetails = normalizeBroker(reListing?.propertyBroker);
  const tenantReadinessEntries = useMemo(
    () => buildTenantReadinessEntries(reListing?.tenantReadiness),
    [reListing]
  );

  //   useEffect(() => {
  //     console.log("useEffect", reListing, allAmenities);
  //     if( reListing && allAmenities?.length > 0) {
  //       setAvailableAmenities(buildAmenityList(reListing, allAmenities));
  //     }

  //   }, [reListing, allAmenities]);

  const lastUpdatedDisplay = formatTimestamp(reListing?.lastModifiedDate);

  const availableImages = useMemo(() => collectListingImages(reListing), [
    reListing
  ]);

  //   useEffect(() => {
  //     let isMounted = true;
  //     const api = useApi();

  //     const hydrateImages = async () => {
  //       if (!availableImages.length) {
  //         if (isMounted) setGalleryItems([]);
  //         return;
  //       }

  //       const resolved = await Promise.all(
  //         availableImages.map(async image => {
  //           const id = resolveImageIdentifier(image);
  //           const original = await api.getImage(id);
  //           const thumbnail = await api.getResponsiveImage(id, "md");
  //           const width =
  //             image?.width ??
  //             image?.metadata?.width ??
  //             image?.thumbnailWidth ??
  //             DEFAULT_IMAGE_DIMENSION;
  //           const height =
  //             image?.height ??
  //             image?.metadata?.height ??
  //             image?.thumbnailHeight ??
  //             DEFAULT_IMAGE_DIMENSION;

  //           return {
  //             id,
  //             original,
  //             thumbnail,
  //             width,
  //             height,
  //             caption: image?.caption ?? listingTitle
  //           };
  //         })
  //       );

  //       if (isMounted) {
  //         setGalleryItems(resolved);
  //       }
  //     };

  //     hydrateImages();

  //     return () => {
  //       isMounted = false;
  //     };
  //   }, [availableImages, listingTitle]);

  const galleryOptions = useMemo(
    () => ({
      padding: { top: "3rem", bottom: "3rem", left: "3rem", right: "3rem" },
      zoom: false
    }),
    []
  );

  return (
    <div
      className="crm-pau-re-listing-view"
      style={{ maxWidth: "1100px", margin: "0 auto" }}
    >
      
      <section className={`listing-gallery ${availableImages.length === 0 ? "empty" : ""}`}>
      {availableImages.length > 0 && (
          <PhotoswipeGallery
            withCaption={true}
            options={{
              ...galleryOptions,
              // add functional padding so images do not reach controls
              padding: { top: 48, bottom: 48, left: 48, right: 48 },
              // forcibly contain image in modal and maintain aspect ratio
              imageFit: "contain",
              bgOpacity: 0.98
              // (if desired you can set other properties relevant to your version)
            }}
            photos={
              availableImages
                ? availableImages.map(item => ({
                    id: item.id,
                    src: `${apiUrl}/api/files/${
                      item.id
                    }/lg`,
                    thumbnail: `${apiUrl}/api/files/${item.id}/thumb`,
                    width: item.width ?? 1600,
                    height: item.height ?? 900,
                    caption: item.caption
                  }))
                : []
            }
          />
          )}
        </section>

      <section className="listing-summary">
        <header className="listing-header">
          <div className="listing-header__primary">
            {status && <span className="listing-status">{status}</span>}
            <h1 className="listing-title">{listingTitle}</h1>
            {addressLabel && <p className="listing-address">{addressLabel}</p>}
          </div>

          <aside className="listing-actions">
            {saleOrLease === "LEASE" && monthlyRent && (
              <div className="listing-price">
                <span className="listing-price__label">Monthly rent</span>
                <span className="listing-price__value">
                  {formatCurrency(monthlyRent)}
                </span>
                {rentPerSqFtYear != null && (
                  <span className="listing-price__label">
                    ${Number(rentPerSqFtYear).toLocaleString()}/SF/yr
                  </span>
                )}
              </div>
            )}
            {saleOrLease === "SALE" && salePrice && (
              <div className="listing-price">
                <span className="listing-price__label">Sale price</span>
                <span className="listing-price__value">
                  {formatCurrency(salePrice)}
                </span>
              </div>
            )}
            {underDevelopment && (
              <div className="listing-price">
                <span className="listing-price__label">Under development</span>
                <span className="listing-price__value">
                  {expectedCompletion
                    ? `Expected ${expectedCompletion}`
                    : "Coming soon"}
                </span>
              </div>
            )}
          </aside>
        </header>

        {statCards.length > 0 && (
          <div className="listing-highlights">
            {statCards.map(entry => (
              <div key={entry.label} className="listing-highlight">
                <span className="listing-highlight__label">{entry.label}</span>
                <span className="listing-highlight__value">{entry.value}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="listing-details">
        <div className="listing-details__column">
          <div className="listing-details__description">
            <h2 className="listing-details__heading">{headline}</h2>
            {descriptionParagraphs.length > 0 ? (
              descriptionParagraphs.map((text, index) => (
                <p key={`desc-${index}`} className="listing-description">
                  {text}
                </p>
              ))
            ) : (
              <p className="listing-details__empty">
                A description has not been provided yet.
              </p>
            )}
          </div>
          <br />
          <div className="listing-details__amenities">
            <h2 className="listing-details__heading">Amenities</h2>
            {reListing?.selectedAmenities?.length > 0 ? (
              <AmenityViewList
                profile={{ amenities: reListing?.selectedAmenities }}
                mode="PAU"
              />
            ) : (
              <p className="listing-details__empty">
                No amenities have been selected yet.
              </p>
            )}
          </div>
        </div>

      {brokerDetails && (
        <div className="listing-details__column listing-details__broker">
          <h2 className="listing-details__heading">Contact Broker/Agent</h2>
          {brokerDetails ? (
            <div className="listing-detail-list">
              {brokerDetails.firstName && brokerDetails.lastName && (
                <span>
                  {brokerDetails.firstName} {brokerDetails.lastName}
                </span>
              )}
              {brokerDetails.organization && (
                <span>{brokerDetails.organization}</span>
              )}
              {brokerDetails.emailAddress && (
                <a
                  className="with-icon"
                  href={`mailto:${brokerDetails.emailAddress}`}
                >
                  <i className="fa-solid fa-envelope"></i>
                  {brokerDetails.emailAddress}
                </a>
              )}
              {brokerDetails.phoneNumber && (
                <a
                  className="with-icon"
                  href={`tel:${utils.sanitizePhoneNumber(
                    brokerDetails.phoneNumber
                  )}`}
                >
                  <i className="fa-solid fa-phone"></i>
                  {brokerDetails.phoneNumber}
                </a>
              )}
              {brokerDetails.notes && (
                <>
                  <span>
                    <b>Notes</b>
                  </span>
                  <span className="small">{brokerDetails.notes}</span>
                </>
              )}
            </div>
          ) : (
            <p className="listing-details__empty">
              Broker contact information has not been added yet.
            </p>
          )}
        </div>
        )}
      </section>

      {!underDevelopment && tenantReadinessEntries.length > 0 && (
      <section className="listing-details">
        <div className="listing-details__column">
          <h2 className="listing-details__heading">Occupancy Status</h2>
            <div className="listing-detail-list">
              {tenantReadinessEntries.map(entry => (
                <div key={entry.label} className="tenant-readiness-item">
                  <div className="tenant-readiness-item__icon">
                    <i className={`fa-solid ${entry.icon ?? "fa-circle-info"}`} />
                  </div>
                  <div className="tenant-readiness-item__content">
                    <span>
                      <b>{entry.label}:</b> {entry.value}
                    </span>
                  </div>
                  {entry.hint && (
                    <div className="small text-muted tenant-readiness-item__hint">
                      {entry.hint}
                    </div>
                  )}
                </div>
              ))}
            </div>
        </div>
      </section>
    )}

      <footer className="listing-footer">
        <span className="listing-footer__timestamp">
          Last updated {lastUpdatedDisplay}
        </span>
        {reListing?.id && (
          <span className="listing-footer__id">Listing ID: {reListing.id}</span>
        )}
      </footer>
    </div>
  );
};

const collectListingImages = listing => {
  if (!listing) return [];

  const images = [
    ...(listing.unitSnapshot?.images ?? []),
    ...(listing.addressSnapshot?.images ?? [])
  ];
  // listings saved through the unit/import upsert can have no selection (null)
  const selectedIds = listing.selectedImages ?? [];
  const selected = images.filter(image => selectedIds.includes(image.id));
  return selected;
};

const resolveImageIdentifier = image => {
  if (!image) return null;
  if (typeof image === "string") return image;

  return (
    image?.id ??
    image?.imageId ??
    image?.assetId ??
    image?.galleryItemId ??
    image?.sourceId ??
    null
  );
};

const normalizeBroker = broker => {
  //   console.log("normalizeBroker", broker);
  if (!broker) return null;
  if (broker.contactSummary) {
    const { firstName, lastName, organization, emailAddress, phoneNumber, notes } = broker.contactSummary;
    // Check if all relevant fields are null, undefined, or blank (empty string or only whitespace)
    const isAllEmpty = [firstName, lastName, organization, emailAddress, phoneNumber, notes].every(
      field => field == null || (typeof field === "string" && field.trim() === "")
    );
    if (isAllEmpty) {
      return null;
    }
  } else {
    return null;
  }

  return broker.contactSummary;
};

const formatEnumValue = value => {
  if (!value) return null;
  return value
    .toString()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/(^|\s)\S/g, char => char.toUpperCase());
};

const formatCurrency = amount => {
  if (amount == null) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(Number(amount));
};

const formatTimestamp = timestamp => {
  if (!timestamp) return "recently";
  try {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return "recently";
    }
    return date.toLocaleString();
  } catch (error) {
    return "recently";
  }
};

const buildListingTitle = listing => {
  if (!listing) return "Listing";

  const unitName = listing.unitSnapshot?.name;
  const addressLabel = listing.addressSnapshot?.addressLine1;

  if (unitName && unitName !== "N/A") {
    return addressLabel ? `${addressLabel} • ${unitName}` : unitName;
  }

  return addressLabel || "Listing";
};

const buildTenantReadinessEntries = tenantReadiness => {
  if (!tenantReadiness) return [];

  const entries = [];

  if (tenantReadiness.certificateOfOccupancyStatus) {
    const option = findOptionByValue(
      CrmPauEnums.tenantReadinessCertificateStatus,
      tenantReadiness.certificateOfOccupancyStatus
    );
    entries.push({
      label: "Certificate of Occupancy Status",
      value:
        option?.label ?? formatEnumValue(tenantReadiness.certificateOfOccupancyStatus),
      hint: option?.hint,
      icon: "fa-file-contract"
    });
  }

  if (tenantReadiness.buildout) {
    const option = findOptionByValue(
      CrmPauEnums.tenantReadinessBuildout,
      tenantReadiness.buildout
    );
    entries.push({
      label: "Buildout",
      value: option?.label ?? formatEnumValue(tenantReadiness.buildout),
      hint: option?.hint,
      icon: "fa-hammer"
    });
  }

  if (tenantReadiness.leaseType) {
    const option = findOptionByValue(
      CrmPauEnums.tenantReadinessLeaseType,
      tenantReadiness.leaseType
    );
    entries.push({
      label: "Lease Type",
      value: option?.label ?? formatEnumValue(tenantReadiness.leaseType),
      hint: option?.hint,
      icon: "fa-file-signature"
    });
  }

  if (tenantReadiness.leaseTypeNotes) {
    entries.push({
      label: "Notes",
      hint: tenantReadiness.leaseTypeNotes,
      icon: "fa-note-sticky"
    });
  }

  if (tenantReadiness.expectedAvailabilityTimeline) {
    const option = findTenantReadinessTimelineOption(
      tenantReadiness.expectedAvailabilityTimeline
    );
    entries.push({
      label: "Expected Availability Timeline",
      value:
        option?.label ??
        formatEnumValue(tenantReadiness.expectedAvailabilityTimeline),
      hint: option?.hint,
      icon: "fa-calendar-days"
    });
  }

  if (tenantReadiness.minLeaseTermMonths != null) {
    entries.push({
      label: "Minimum Lease Term",
      value: `${tenantReadiness.minLeaseTermMonths} months`,
      icon: "fa-hourglass-start"
    });
  }

  if (tenantReadiness.maxLeaseTermMonths != null) {
    entries.push({
      label: "Maximum Lease Term",
      value: `${tenantReadiness.maxLeaseTermMonths} months`,
      icon: "fa-hourglass-end"
    });
  }

  if (tenantReadiness.changeInOccupancyTriggersUpgrades != null) {
    entries.push({
      label: "Change in Occupancy Triggers Upgrades",
      value: tenantReadiness.changeInOccupancyTriggersUpgrades ? "Yes" : "No",
      hint: tenantReadiness.changeInOccupancyTriggersUpgrades
        ? "A tenant-use change can trigger required code or building upgrades."
        : "A tenant-use change does not currently trigger required code or building upgrades.",
      icon: "fa-triangle-exclamation"
    });
  }

  if (tenantReadiness.ownerWillPayForImprovements != null) {
    entries.push({
      label: "Owner Will Pay For Improvements",
      value: tenantReadiness.ownerWillPayForImprovements ? "Yes" : "No",
      hint: tenantReadiness.ownerWillPayForImprovements
        ? "The owner will contribute to some or all tenant improvement costs."
        : "The owner is not contributing to tenant improvement costs.",
      icon: "fa-hand-holding-dollar"
    });
  }

  if (tenantReadiness.purchaseOption != null) {
    entries.push({
      label: "Purchase Option",
      value: tenantReadiness.purchaseOption ? "Yes" : "No",
      hint: tenantReadiness.purchaseOption
        ? "The lease terms include an option for the tenant to purchase the property."
        : "The lease terms do not include a purchase option.",
      icon: "fa-key"
    });
  }

  return entries;
};

const findOptionByValue = (options, value) => {
  if (!Array.isArray(options) || !value) return null;
  return options.find(option => option.value === value) ?? null;
};

const findTenantReadinessTimelineOption = value => {
  if (!value) return null;
  const byDirectMatch = findOptionByValue(
    CrmPauEnums.tenantReadinessAvailabilityTimeline,
    value
  );
  if (byDirectMatch) return byDirectMatch;

  // Backward/forward compatibility for timeline value naming variants.
  const aliasMap = {
    "30_60_DAYS": "DAYS_30_60",
    "60_90_DAYS": "DAYS_60_90",
    "90_PLUS_DAYS": "DAYS_90_PLUS",
    DAYS_30_60: "30_60_DAYS",
    DAYS_60_90: "60_90_DAYS",
    DAYS_90_PLUS: "90_PLUS_DAYS"
  };
  const mappedValue = aliasMap[value];
  return mappedValue
    ? findOptionByValue(CrmPauEnums.tenantReadinessAvailabilityTimeline, mappedValue)
    : null;
};

export default CrmPauREListingView;
